"use client";

import { type KeyboardEvent, useRef, useState } from "react";
import {
  Briefcase,
  ChartNoAxesCombined,
  Code2,
  FileText,
  Landmark,
  ShoppingCart,
  type LucideIcon,
} from "lucide-react";
import { useTranslations } from "next-intl";

import SectionHeading from "./section-heading";

const DEFAULT_TAB: TabKey = "ecommerce";

const TABS: ReadonlyArray<{ key: TabKey; icon: LucideIcon }> = [
  { key: "ecommerce", icon: ShoppingCart },
  { key: "competitors", icon: ChartNoAxesCombined },
  { key: "regulation", icon: Landmark },
  { key: "tenders", icon: FileText },
  { key: "jobs", icon: Briefcase },
  { key: "dependencies", icon: Code2 },
];

type TabKey =
  | "ecommerce"
  | "competitors"
  | "regulation"
  | "tenders"
  | "jobs"
  | "dependencies";

/**
 * "What you point it at": one vertical at a time, each answering the same four
 * questions — which item, which fields, who acts, and on what decision — so an
 * abstract "item" becomes the reader's own product, notice or posting.
 */
export default function PointItAtSection() {
  const t = useTranslations("pointItAt");
  const [active, setActive] = useState<TabKey>(DEFAULT_TAB);
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    const current = TABS.findIndex((tab) => tab.key === active);
    const next = nextTabIndex(event.key, current, TABS.length);
    if (next === null) {
      return;
    }
    const tab = TABS[next];
    if (!tab) {
      return;
    }
    event.preventDefault();
    setActive(tab.key);
    tabRefs.current[next]?.focus();
  };

  return (
    <section id="use-cases" className="scroll-mt-24 bg-background py-20 md:py-24">
      <div className="container mx-auto px-4 md:px-6">
        <SectionHeading
          eyebrow={t("eyebrow")}
          title={t("title")}
          description={t("description")}
          eyebrowClassName="text-muted-foreground"
        />
        <div className="mx-auto max-w-5xl">
          <div
            role="tablist"
            aria-label={t("eyebrow")}
            onKeyDown={handleKeyDown}
            className="flex gap-2 overflow-x-auto pb-2"
          >
            {TABS.map(({ key, icon: Icon }, index) => {
              const selected = key === active;
              return (
                <button
                  key={key}
                  ref={(element) => {
                    tabRefs.current[index] = element;
                  }}
                  type="button"
                  role="tab"
                  id={`point-tab-${key}`}
                  aria-selected={selected}
                  aria-controls="point-panel"
                  tabIndex={selected ? 0 : -1}
                  onClick={() => setActive(key)}
                  className={`inline-flex shrink-0 items-center gap-2 rounded-full border px-4 py-2 text-sm font-semibold transition focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 ${
                    selected
                      ? "border-primary bg-primary text-primary-foreground"
                      : "border-border bg-card text-muted-foreground hover:text-foreground"
                  }`}
                >
                  <Icon className="h-4 w-4" aria-hidden="true" />
                  {t(`tabs.${key}.label`)}
                </button>
              );
            })}
          </div>
          <TabPanel tabKey={active} />
        </div>
      </div>
    </section>
  );
}

function nextTabIndex(
  key: string,
  current: number,
  count: number,
): number | null {
  switch (key) {
    case "ArrowRight":
      return (current + 1) % count;
    case "ArrowLeft":
      return (current - 1 + count) % count;
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return null;
  }
}

function TabPanel({ tabKey }: { tabKey: TabKey }) {
  const t = useTranslations("pointItAt");
  const fields = t.raw(`tabs.${tabKey}.fields`) as string[];

  return (
    <div
      role="tabpanel"
      id="point-panel"
      aria-labelledby={`point-tab-${tabKey}`}
      className="mt-6 grid gap-6 rounded-3xl border border-border bg-card p-6 shadow-sm md:grid-cols-2 md:p-8"
    >
      <div>
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("itemLabel")}
        </p>
        <h3 className="mt-1 text-2xl font-bold text-foreground">
          {t(`tabs.${tabKey}.item`)}
        </h3>
        <p className="mt-3 leading-relaxed text-muted-foreground">
          {t(`tabs.${tabKey}.body`)}
        </p>
        <p className="mt-5 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("fieldsLabel")}
        </p>
        <ul className="mt-2 flex flex-wrap gap-2">
          {fields.map((field) => (
            <li
              key={field}
              className="rounded-md border border-border bg-muted px-2.5 py-1 font-mono text-xs text-foreground"
            >
              {field}
            </li>
          ))}
        </ul>
      </div>
      <div className="rounded-2xl bg-muted p-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("whoLabel")}
        </p>
        <p className="mt-1 text-lg font-semibold text-foreground">
          {t(`tabs.${tabKey}.who`)}
        </p>
        <p className="mt-4 text-xs font-semibold uppercase tracking-widest text-muted-foreground">
          {t("decisionLabel")}
        </p>
        <p className="mt-1 leading-relaxed text-foreground">
          {t(`tabs.${tabKey}.decision`)}
        </p>
      </div>
    </div>
  );
}
